import _ from 'lodash';
import React, { useCallback, useContext, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { ViewModelForm, useDataRefresh, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { TranslatorContext } from '@jutro/locale';
import { useModal } from '@jutro/components';
import { CheckboxField } from '@jutro/legacy/components';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import { PolicyholderRegistrationService } from "ent-capability-policyholderregistration";
import metadata from './PolicyholderRegistrationPage.metadata.json5';
import styles from './PolicyholderRegistrationPage.module.scss';
import messages from './PolicyholderRegistrationPage.messages.js';

function PolicyholderRegistrationPage(props){

    const { id } = props;
    const { onValidate, isComponentValid } = useValidation(id);
    const [showErrors, setShowErrors] = useState(false);
    const translator = useContext(TranslatorContext);
    const viewModelService = useContext(ViewModelServiceContext);
    const [registrationVM, setRegistrationVM] = useState();
    const { refreshData } = useDataRefresh();
    const { showAlert } = useModal();
    const [captchaValue, setCaptchaValue] = useState();
    // US29021 TFPA vs TWIA switch for branding
    const [isTFPA, setIsTFPA] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const queryString = require('query-string');
    const parsedParams = queryString.parse(window.location.search);
    const [checkBoxState, setCheckBoxState] = useState(false);
    const history = useHistory();

    useEffect(() =>{
        if (viewModelService && !registrationVM) {
            // eslint-disable-next-line no-secrets/no-secrets
            const dto = 'org.twia.edge.capabilities.policyholder.registration.dto.TWIASignupAndEnrollDTO';
            const viewModel = viewModelService.create({}, 'pc', dto);

            const { emailTo } = parsedParams;

            if(emailTo){
                viewModel.email.value = emailTo;
                viewModel.confirmationEmail.value = emailTo;
            }

            setRegistrationVM(viewModel);

            // US29021 TFPA vs TWIA switch for branding
            const tfpa = window.location.href.includes('signup-tfpa');

            viewModel.uwcompany.value = tfpa ? '1001_00001' : '2001_00001';
            setIsTFPA(tfpa);
        }
    },[viewModelService, registrationVM, isComponentValid, parsedParams])

    const writeValue = useCallback((value, path) => {
        _.set(registrationVM, `${path}.value`, value);
        setRegistrationVM(registrationVM);
        refreshData();
    },
    [registrationVM, setRegistrationVM, refreshData]
  );

    const createRegistration = useCallback(() => {
        const headers = {
            'x-captcha': captchaValue,
        };

        const missingPolicyOroffer = isTFPA ? 
            messages.policyholderRegistrationInvalidPolicyOrOfferNumberTFPA :
            messages.policyholderRegistrationInvalidPolicyOrOfferNumber;

        if (!isComponentValid || registrationVM.email.value !== registrationVM.confirmationEmail.value || !registrationVM.termsAndConditionsResponse.value) {
            setShowErrors(true);

        // 2025-03-05 - Debra Frum - DE18619 - Display TFPA instead of TWIA validation message when registering for TFPA
        // Defined separate PolicyOrOfferNumber fields for TWIA and TFPA in the shared PC DTO so that unique field  
        // validation messages can be defined in the DTO.
        if(( isTFPA && !registrationVM.policyOrOfferNumber_TFPA.aspects.valid)
            || (!isTFPA || !registrationVM.policyOrOfferNumber_TWIA.aspects.valid)){
                showAlert({
                    title: messages.policyholderRegistrationInvalidPolicyOrOfferNumberTitle,
                    message: missingPolicyOroffer,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: commonMessages.close,
                }).catch(_.noop);
            }
        } else {
            setShowErrors(false);

            const duplicateError = isTFPA ? 
                messages.policyholderRegistrationDuplicateRegistrationErrorTFPA :
                messages.policyholderRegistrationDuplicateRegistrationError;

            setIsLoading(true);
            PolicyholderRegistrationService.registerAndEnroll(registrationVM.value, headers).then(response => {
                window.location.href = response;
            }).catch(err => {
                const errorCode = _.get(err, 'data.appErrorCode');
                let errorMessage; 
                let errorTitle;

                if(errorCode === 10030){
                    errorTitle = messages.policyholderRegistrationInvalidPolicyOrOfferNumberTitle;
                    errorMessage = missingPolicyOroffer;
                }else if(errorCode === 10050 || errorCode === 10051){
                    errorTitle = commonMessages.genericError               
                    errorMessage = translator(
                        duplicateError, 
                        {policyOrOfferNumber: 
                              isTFPA 
                            ? registrationVM.policyOrOfferNumber_TFPA.value
                            : registrationVM.policyOrOfferNumber_TWIA.value});
        
                }else if(errorCode === 10040){         
                    errorTitle = messages.policyholderRegistrationInvalidDataTitle;                             
                    errorMessage = messages.policyholderRegistrationInvalidData;                
                }else {       
                    errorTitle = messages.policyholderRegistrationErrorTitle;                                
                    errorMessage = messages.policyholderRegistrationError;
                }

                showAlert({
                    title: errorTitle,
                    message: errorMessage,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: commonMessages.close,
                }).catch(_.noop);
                // eslint-disable-next-line no-console
                console.error(`createRegistration() showAlert error. errorCode=${errorCode}`);
            }).finally(() => {
                setIsLoading(false);
            });
        }
    }, [captchaValue, isComponentValid, registrationVM, isTFPA, showAlert, translator]);


    const resetRegistration = useCallback(() => {
        // eslint-disable-next-line no-secrets/no-secrets
        const dto = 'org.twia.edge.capabilities.policyholder.registration.dto.TWIASignupAndEnrollDTO';
        const viewModel = viewModelService.create({}, 'pc', dto);

        setRegistrationVM(viewModel);
        setShowErrors(false);
        setCaptchaValue(false);
        setCheckBoxState(false);
        /**
         * to actually re-render the captcha
         * widget - we have to refresh the page.
         */
        history.go(0);
    }, [history, viewModelService]);

    const handleCaptchaError = useCallback(error => {
        if (error.id === 'noAPIKey') {
            setCaptchaValue(true);
        } else {
            throw new Error(error);
        }
    }, []);    

    /**
     * Requires Checkbox rendering via JSX 
     * because we need to render a hyperlink.
     * Need function declaration, instead of arrow function
     * due to when this function can be invoked/resolved.
     * This is also why we use 'checkBoxState' state value
     * and initialize it to false.
     * DE17321
     * @returns 
     */
    function termsAndConditionsCheckbox(){
        const link = translator(messages.policyholderRegistrationTermsAndConditionLink);
        const termsAndConditionsPart1 = isTFPA ? 
            messages.policyholderRegistrationTermsAndConditionsTFPAPart1 :
            messages.policyholderRegistrationTermsAndConditionsPart1;

        return (
            <div style={{ display: "flex", "paddingTop": "1.25rem" }}>                                                

                <CheckboxField
                id="checkbox-field"
                path="termsAndConditionsResponse"
                value={checkBoxState}
                onValueChange={(evt) =>{handleClick(evt)}}
                required />

                <p style={{"paddingLeft": ".5rem" }}>                
                {translator(termsAndConditionsPart1)}
                <a href={link} target="_blank" rel="noreferrer">
                {translator(messages.policyholderRegistrationTermsAndConditionsPart2)}</a>
                {translator(messages.policyholderRegistrationTermsAndConditionsPart3)}
                </p>
            </div>
        )
    }    

    const handleClick = useCallback(
        (evt) => {
            registrationVM.termsAndConditionsResponse.value = evt;
            setCheckBoxState(evt);                     
            setRegistrationVM(registrationVM);

        },
        [registrationVM]
    );

    const goToSignIn = () => {
        window.location.pathname = window.location.pathname.replace(isTFPA ? '/signup-tfpa' : '/signup', '/home');
    };
        
    

    const overrideProps = {
        '@field': {
            showErrors,
            showRequired: true
          },                
        requiredFieldsNotification: {
            visible: showErrors
        },
        portalImg: {
            visible: true,
            src: isTFPA ? 'branding/TFPA_Logo_White.png' : 'twia/TWIA-Logo-White.png'
        },
        termsAndConditionsCheckboxDiv: {
            visible: true,
            content: termsAndConditionsCheckbox()
        },

        // 2025-03-05 - Debra Frum - DE18619 - Display TFPA instead of TWIA validation message when registering for TFPA
        // Defined separate PolicyOrOfferNumber fields for TWIA and TFPA in the shared PC DTO so that unique field  
        // validation messages can be defined in the DTO.
        policyOrOfferNumber_TFPA: {
            visible : isTFPA,
            tooltip: {
                text: translator(messages.policyOrOfferToolTipTFPA) 
            }
        },
        policyOrOfferNumber_TWIA: {
            visible : !isTFPA,
            tooltip: {
                text: translator(messages.policyOrOfferToolTip)
            }
        },

        registerSubmitLoader: {
            visible: isLoading
        },
        submitButton: {
            visible: !isLoading
        },
        resetButton: {
            visible: !isLoading
        },
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
             createRegistration,
             resetRegistration,
             handleCaptchaChange: setCaptchaValue,
             handleCaptchaError,             
             onValidate,
             goToSignIn
        }
      }

    return(
        <ViewModelForm
                uiProps={metadata.pageContent}
                model={registrationVM}
                overrideProps={overrideProps}
                classNameMap={resolvers.resolveClassNameMap}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}                
                onValidationChange={onValidate}
                onValueChange={writeValue}
            />         
    );
}

export default PolicyholderRegistrationPage;
